import { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, ShoppingCart, Package, Clock, CheckCircle, Truck, 
  XCircle, Factory, Plus, ArrowRight, Users, RefreshCw, Download, Trash2, AlertTriangle, Upload, FileSpreadsheet, Settings, Table, Loader2, PackageCheck
} from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import LoadingOverlay from '../components/LoadingOverlay';
import ErrorState from '../components/ErrorState';
import UploadProgress from '../components/UploadProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to format contribution date
const formatContribDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    // Show time for today
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  in_production: { label: 'In Production', color: 'bg-purple-100 text-purple-700', icon: Factory },
  shipped: { label: 'Shipped', color: 'bg-cyan-100 text-cyan-700', icon: Truck },
  partial_delivered: { label: 'Partial Delivered', color: 'bg-teal-100 text-teal-700', icon: PackageCheck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const Orders = () => {
  const { getAuthHeaders, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showContributeDialog, setShowContributeDialog] = useState(false);
  const [contributeQty, setContributeQty] = useState('');
  const [contributeNotes, setContributeNotes] = useState('');
  const [contributing, setContributing] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Export state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportStatus, setExportStatus] = useState('all');
  
  // Clear orders state
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearingOrders, setClearingOrders] = useState(false);

  // Upload PO state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadConfig, setUploadConfig] = useState({
    headerLine: 1,
    itemNameColumn: 1,
    skuColumn: 0,
    quantityColumn: 2
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Bulk selection state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Delivery dialog state
  const [showDeliveryDialog, setShowDeliveryDialog] = useState(false);
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [deliveryQty, setDeliveryQty] = useState('');
  const [deliveryDepot, setDeliveryDepot] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [recordingDelivery, setRecordingDelivery] = useState(false);
  const [depots, setDepots] = useState([]);

  const isFactoryAdmin = user?.role === 'factory_admin';
  const isAdmin = user?.role === 'admin';
  const canUpdateStatus = isFactoryAdmin || isAdmin;

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeaders() });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Unable to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await axios.get(`${API}/depots`, { headers: getAuthHeaders() });
      setDepots(response.data);
    } catch (error) {
      console.error('Error fetching depots:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    if (canUpdateStatus) {
      fetchDepots();
    }
  }, [getAuthHeaders]);

  const filteredOrders = orders.filter(order => {
    const matchesSearch = search.trim() === '' || 
      order.item_name.toLowerCase().includes(search.toLowerCase()) ||
      order.sku.toLowerCase().includes(search.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Export to Excel function
  const exportToExcel = () => {
    // Filter orders based on selected export status
    const ordersToExport = exportStatus === 'all' 
      ? orders 
      : orders.filter(o => o.status === exportStatus);
    
    if (ordersToExport.length === 0) {
      toast.error('No orders to export for the selected status');
      return;
    }
    
    // Prepare data for export
    const exportData = ordersToExport.map(order => {
      // Flatten contributions
      const contributionsStr = order.contributions
        ?.map(c => `${c.depot_name}: ${c.quantity}`)
        .join(', ') || '';
      
      return {
        'Item Name': order.item_name,
        'SKU': order.sku,
        'Status': STATUS_CONFIG[order.status]?.label || order.status,
        'Total Quantity': order.total_quantity,
        'Contributors': order.contributions?.length || 0,
        'Contributions': contributionsStr,
        'Created By': order.created_by_name,
        'Created At': new Date(order.created_at).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        }),
        'Last Updated': new Date(order.updated_at).toLocaleDateString('en-IN', {
          day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
        }),
        'Notes': order.notes || ''
      };
    });
    
    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 35 },  // Item Name
      { wch: 18 },  // SKU
      { wch: 15 },  // Status
      { wch: 12 },  // Total Quantity
      { wch: 12 },  // Contributors
      { wch: 40 },  // Contributions
      { wch: 20 },  // Created By
      { wch: 22 },  // Created At
      { wch: 22 },  // Last Updated
      { wch: 30 }   // Notes
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Reorders');
    
    // Generate filename
    const statusLabel = exportStatus === 'all' ? 'all_status' : exportStatus;
    const filename = `reorders_${statusLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;
    
    XLSX.writeFile(wb, filename);
    toast.success(`Exported ${ordersToExport.length} orders to Excel`);
    setShowExportDialog(false);
  };

  const handleContribute = async () => {
    if (!contributeQty || parseInt(contributeQty) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setContributing(true);
    try {
      await axios.post(
        `${API}/orders/${selectedOrder.id}/contribute`,
        { quantity: parseInt(contributeQty), notes: contributeNotes || null },
        { headers: getAuthHeaders() }
      );
      toast.success('Contribution added successfully');
      setShowContributeDialog(false);
      setContributeQty('');
      setContributeNotes('');
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contribution');
    } finally {
      setContributing(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success(`Order status updated to ${STATUS_CONFIG[newStatus].label}`);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleClearAllOrders = async () => {
    setClearingOrders(true);
    try {
      const response = await axios.post(`${API}/clear-orders`, {}, { headers: getAuthHeaders() });
      toast.success(`Cleared ${response.data.total_cleared} orders successfully`);
      setShowClearDialog(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear orders');
    } finally {
      setClearingOrders(false);
    }
  };

  // Bulk selection handlers
  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders.map(o => o.id));
    }
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedOrders.length === 0) {
      toast.error('Please select orders first');
      return;
    }
    
    setBulkUpdating(true);
    try {
      const response = await axios.put(
        `${API}/orders/bulk-status`,
        { order_ids: selectedOrders, status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success(`Updated ${response.data.updated_count} orders to ${STATUS_CONFIG[newStatus].label}`);
      setSelectedOrders([]);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update orders');
    } finally {
      setBulkUpdating(false);
    }
  };

  // Delivery handlers
  const openDeliveryDialog = (order) => {
    const totalDelivered = order.total_delivered || 0;
    const remaining = order.total_quantity - totalDelivered;
    setDeliveryOrder({ ...order, remaining });
    setDeliveryQty(remaining.toString());
    setDeliveryDepot('');
    setDeliveryNotes('');
    setShowDeliveryDialog(true);
  };

  const handleRecordDelivery = async () => {
    if (!deliveryOrder || !deliveryQty) {
      toast.error('Please enter delivery quantity');
      return;
    }
    
    const qty = parseInt(deliveryQty);
    if (isNaN(qty) || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    if (qty > deliveryOrder.remaining) {
      toast.error(`Quantity cannot exceed remaining (${deliveryOrder.remaining})`);
      return;
    }
    
    setRecordingDelivery(true);
    try {
      const response = await axios.post(
        `${API}/orders/${deliveryOrder.id}/deliveries`,
        { 
          quantity: qty, 
          depot_id: deliveryDepot || null,
          notes: deliveryNotes || null
        },
        { headers: getAuthHeaders() }
      );
      
      const statusMsg = response.data.remaining === 0 ? 'Fully delivered!' : `${response.data.remaining} units remaining`;
      toast.success(`Recorded delivery of ${qty} units. ${statusMsg}`);
      setShowDeliveryDialog(false);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record delivery');
    } finally {
      setRecordingDelivery(false);
    }
  };

  // Upload PO handlers
  const handlePOFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }
    
    setUploadFile(file);
    setPreviewLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/orders/preview-po`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadPreview(response.data);
      
      // Auto-detect columns based on first row (header) content
      if (response.data.preview_rows && response.data.preview_rows.length > 0) {
        const headerRow = response.data.preview_rows[0]?.data || [];
        let detectedConfig = { ...uploadConfig };
        
        headerRow.forEach((cell, idx) => {
          const name = (cell || '').toLowerCase();
          if (name.includes('item') || name.includes('product') || name.includes('name')) {
            if (!name.includes('sku') && !name.includes('code')) {
              detectedConfig.itemNameColumn = idx + 1;
            }
          }
          if (name.includes('sku') || (name.includes('code') && !name.includes('name'))) {
            detectedConfig.skuColumn = idx + 1;
          }
          if (name.includes('qty') || name.includes('quantity') || name.includes('order qty')) {
            detectedConfig.quantityColumn = idx + 1;
          }
        });
        
        setUploadConfig(detectedConfig);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview file');
      setUploadFile(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Get mapped preview data for PO
  const getMappedPOPreview = () => {
    if (!uploadPreview?.preview_rows) return [];
    
    const dataRows = uploadPreview.preview_rows.filter(r => r.line > uploadConfig.headerLine);
    return dataRows.slice(0, 5).map(row => ({
      itemName: row.data[uploadConfig.itemNameColumn - 1] || '',
      quantity: row.data[uploadConfig.quantityColumn - 1] || '',
      sku: uploadConfig.skuColumn > 0 ? row.data[uploadConfig.skuColumn - 1] || '' : ''
    }));
  };

  const handleUploadPO = async () => {
    if (!uploadFile) {
      toast.error('Please select a file first');
      return;
    }
    
    if (uploadConfig.itemNameColumn < 1 || uploadConfig.quantityColumn < 1) {
      toast.error('Please configure Item Name and Quantity columns');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('header_line', uploadConfig.headerLine);
      formData.append('item_name_column', uploadConfig.itemNameColumn);
      formData.append('sku_column', uploadConfig.skuColumn);
      formData.append('quantity_column', uploadConfig.quantityColumn);
      
      const response = await axios.post(`${API}/orders/upload-po`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });
      
      toast.success(`Created ${response.data.orders_created} orders from purchase order`);
      resetUploadDialog();
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload purchase order');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadConfig({ headerLine: 1, itemNameColumn: 1, skuColumn: 0, quantityColumn: 2 });
  };

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const completedOrders = orders.filter(o => ['delivered', 'cancelled'].includes(o.status));

  if (loading) {
    return <LoadingOverlay message="Loading orders..." />;
  }

  if (error) {
    return <ErrorState title="Failed to load orders" message={error} onRetry={fetchOrders} />;
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reorders
          </h1>
          <p className="text-slate-500 mt-1">Manage stock reorder requests across depots</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            onClick={() => setShowUploadDialog(true)} 
            className="bg-green-600 hover:bg-green-700"
            data-testid="upload-po-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload PO
          </Button>
          {isAdmin && (
            <Button 
              onClick={() => setShowClearDialog(true)} 
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
              data-testid="clear-orders-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Reset Orders
            </Button>
          )}
          <Button onClick={() => setShowExportDialog(true)} variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
          <Button onClick={fetchOrders} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Orders</p>
                <p className="text-2xl font-bold text-slate-900">{orders.length}</p>
              </div>
              <div className="bg-blue-100 p-2 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Active Orders</p>
                <p className="text-2xl font-bold text-yellow-600">{activeOrders.length}</p>
              </div>
              <div className="bg-yellow-100 p-2 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Production</p>
                <p className="text-2xl font-bold text-purple-600">
                  {orders.filter(o => o.status === 'in_production').length}
                </p>
              </div>
              <div className="bg-purple-100 p-2 rounded-lg">
                <Factory className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Delivered</p>
                <p className="text-2xl font-bold text-green-600">
                  {orders.filter(o => o.status === 'delivered').length}
                </p>
              </div>
              <div className="bg-green-100 p-2 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by Item Name or SKU..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>
                    <span className="flex items-center gap-2">
                      <config.icon className="w-3 h-3" />
                      {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Action Bar (Factory Admin / Admin only) */}
      {canUpdateStatus && selectedOrders.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-sm">
                  {selectedOrders.length} order{selectedOrders.length > 1 ? 's' : ''} selected
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => setSelectedOrders([])}>
                  Clear Selection
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 mr-2">Change status to:</span>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    onClick={() => handleBulkStatusUpdate(key)}
                    disabled={bulkUpdating}
                    className={`${config.color} border-current hover:opacity-80`}
                    data-testid={`bulk-status-${key}`}
                  >
                    <config.icon className="w-3 h-3 mr-1" />
                    {config.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Orders List */}
      <div className="space-y-4">
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No orders found</p>
              <p className="text-slate-400 text-sm">
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create orders from Inventory HQ'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Select All Header (for Factory Admin / Admin) */}
            {canUpdateStatus && (
              <div className="flex items-center gap-2 px-2">
                <input
                  type="checkbox"
                  checked={selectedOrders.length === filteredOrders.length && filteredOrders.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-slate-300"
                  data-testid="select-all-orders"
                />
                <span className="text-sm text-slate-500">Select All ({filteredOrders.length})</span>
              </div>
            )}
            {filteredOrders.map(order => {
              const StatusIcon = STATUS_CONFIG[order.status]?.icon || Clock;
              const isSelected = selectedOrders.includes(order.id);
              return (
                <Card key={order.id} className={`card-hover ${isSelected ? 'ring-2 ring-blue-400 bg-blue-50/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      {/* Checkbox (for Factory Admin / Admin) */}
                      {canUpdateStatus && (
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectOrder(order.id)}
                            className="w-4 h-4 rounded border-slate-300"
                            data-testid={`select-order-${order.id}`}
                          />
                        </div>
                      )}
                      
                      {/* Order Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg text-slate-900">{order.item_name}</h3>
                          <Badge className={`${STATUS_CONFIG[order.status]?.color} border-0`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {STATUS_CONFIG[order.status]?.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-500 space-y-1">
                          <p><span className="font-medium">SKU:</span> {order.sku}</p>
                          <p><span className="font-medium">Created by:</span> {order.created_by_name} on {new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>

                      {/* Quantity & Delivery Progress */}
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-3xl font-bold text-blue-600">{order.total_quantity.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Total Qty</p>
                        </div>
                        {(order.total_delivered > 0 || order.deliveries?.length > 0) && (
                          <div className="text-center">
                            <p className="text-xl font-bold text-green-600">
                              {(order.total_delivered || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-500">Delivered</p>
                          </div>
                        )}
                        {order.status !== 'delivered' && order.status !== 'cancelled' && (order.total_delivered > 0 || order.deliveries?.length > 0) && (
                          <div className="text-center">
                            <p className="text-xl font-bold text-orange-600">
                              {(order.total_quantity - (order.total_delivered || 0)).toLocaleString()}
                            </p>
                            <p className="text-xs text-slate-500">Remaining</p>
                          </div>
                        )}
                        <div className="text-center">
                          <p className="text-xl font-bold text-slate-700">{order.contributions?.length || 1}</p>
                          <p className="text-xs text-slate-500">Contributors</p>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {!['delivered', 'cancelled'].includes(order.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowContributeDialog(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add Qty
                          </Button>
                        )}
                        
                        {/* Record Delivery Button (Factory Admin / Admin only) */}
                        {canUpdateStatus && !['pending', 'delivered', 'cancelled'].includes(order.status) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => openDeliveryDialog(order)}
                            data-testid={`record-delivery-${order.id}`}
                          >
                            <PackageCheck className="w-4 h-4 mr-1" />
                            Record Delivery
                          </Button>
                        )}
                        
                        {canUpdateStatus && (
                          <Select
                            value={order.status}
                            onValueChange={(newStatus) => handleStatusUpdate(order.id, newStatus)}
                          disabled={updatingStatus}
                        >
                          <SelectTrigger className="w-[150px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                <span className="flex items-center gap-2">
                                  <config.icon className="w-3 h-3" />
                                  {config.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>

                  {/* Contributions */}
                  {order.contributions && order.contributions.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Contributions
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {order.contributions.map((contrib, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-slate-100 py-1.5">
                            <span className="font-medium">{contrib.depot_name}:</span>
                            <span className="ml-1">{contrib.quantity.toLocaleString()}</span>
                            <span className="text-slate-400 ml-1">by {contrib.added_by_name}</span>
                            {contrib.added_at && (
                              <span className="text-slate-400 ml-1">· {formatContribDate(contrib.added_at)}</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Deliveries */}
                  {order.deliveries && order.deliveries.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                        <PackageCheck className="w-4 h-4 text-green-600" />
                        Delivery History
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {order.deliveries.map((delivery, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-green-50 text-green-700 py-1.5">
                            <span className="font-medium">{delivery.quantity.toLocaleString()} units</span>
                            {delivery.depot_name && (
                              <span className="ml-1">to {delivery.depot_name}</span>
                            )}
                            <span className="text-green-500 ml-1">by {delivery.delivered_by_name}</span>
                            {delivery.delivered_at && (
                              <span className="text-green-500 ml-1">· {formatContribDate(delivery.delivered_at)}</span>
                            )}
                            {delivery.notes && (
                              <span className="text-green-500 ml-1">· {delivery.notes}</span>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
          </>
        )}
      </div>

      {/* Contribute Dialog */}
      <Dialog open={showContributeDialog} onOpenChange={setShowContributeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Quantity to Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedOrder && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-medium">{selectedOrder.item_name}</p>
                <p className="text-sm text-slate-500">Current Total: {selectedOrder.total_quantity.toLocaleString()}</p>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Quantity to Add</label>
              <Input
                type="number"
                placeholder="Enter quantity"
                value={contributeQty}
                onChange={(e) => setContributeQty(e.target.value)}
                min="1"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <Input
                placeholder="Add a note..."
                value={contributeNotes}
                onChange={(e) => setContributeNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContributeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleContribute} disabled={contributing}>
              {contributing ? 'Adding...' : 'Add Contribution'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Reorders to Excel
            </DialogTitle>
            <DialogDescription>
              Select which orders to export based on status
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Status to Export</label>
              <Select value={exportStatus} onValueChange={setExportStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    <span className="flex items-center gap-2">
                      All Status ({orders.length} orders)
                    </span>
                  </SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                    const count = orders.filter(o => o.status === key).length;
                    return (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <config.icon className="w-3 h-3" />
                          {config.label} ({count} orders)
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div className="bg-slate-50 p-4 rounded-lg text-sm">
              <p className="font-medium mb-2">Export will include:</p>
              <ul className="text-slate-600 space-y-1">
                <li>• Item Name & SKU</li>
                <li>• Status & Total Quantity</li>
                <li>• All Contributions (depot & quantity)</li>
                <li>• Created By & Timestamps</li>
                <li>• Notes</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" />
              Export {exportStatus === 'all' ? orders.length : orders.filter(o => o.status === exportStatus).length} Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear All Orders Dialog */}
      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Reset All Orders
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All orders will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <p className="font-medium text-red-800 mb-2">This will delete:</p>
              <ul className="text-red-700 text-sm space-y-1">
                <li>• All reorders (pending, approved, in production, shipped, delivered)</li>
                <li>• All bulk depot orders</li>
                <li>• Complete order history</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="font-medium text-green-800 mb-2">This will NOT affect:</p>
              <ul className="text-green-700 text-sm space-y-1">
                <li>• Stock levels in Inventory HQ</li>
                <li>• Sales data</li>
                <li>• Buffer stock settings</li>
                <li>• Users and depots</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleClearAllOrders} 
              disabled={clearingOrders}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-clear-orders-btn"
            >
              {clearingOrders ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Yes, Clear All Orders
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Purchase Order Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={resetUploadDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Upload Purchase Order - Configure Column Mapping
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file and map columns to create multiple reorders
            </DialogDescription>
          </DialogHeader>
          
          {/* File Upload Section */}
          {!uploadPreview && !previewLoading && (
            <div className="space-y-4 py-4">
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-sm font-medium mb-2">Select Excel File</p>
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handlePOFileSelect}
                  className="max-w-xs mx-auto"
                  data-testid="po-file-input"
                />
                <p className="text-xs text-slate-400 mt-2">Supported: .xlsx, .xls</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-2">Excel File Requirements:</p>
                <ul className="text-blue-700 space-y-1">
                  <li>• <strong>Item Name</strong> column (required)</li>
                  <li>• <strong>SKU</strong> column (optional - will auto-generate if missing)</li>
                  <li>• <strong>Order Qty</strong> column (required)</li>
                </ul>
              </div>
            </div>
          )}

          {previewLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              <span className="ml-3 text-slate-500">Loading file preview...</span>
            </div>
          )}

          {uploadPreview && (
            <div className="space-y-6">
              {/* File Info */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <FileSpreadsheet className="w-8 h-8 text-green-500" />
                <div>
                  <p className="font-medium">{uploadPreview.filename}</p>
                  <p className="text-sm text-slate-500">
                    {uploadPreview.total_rows} rows × {uploadPreview.total_columns} columns
                  </p>
                </div>
              </div>

              {/* Mapping Configuration */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Header Line</Label>
                  <Select 
                    value={uploadConfig.headerLine.toString()} 
                    onValueChange={(v) => setUploadConfig(prev => ({ ...prev, headerLine: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="po-header-line-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: Math.min(uploadPreview.preview_rows?.length || 15, 15) }, (_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>Line {i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">Data starts from line {uploadConfig.headerLine + 1}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Item Name Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={uploadConfig.itemNameColumn.toString()} 
                    onValueChange={(v) => setUploadConfig(prev => ({ ...prev, itemNameColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="po-item-name-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {uploadPreview.column_letters?.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Order Qty Column <span className="text-red-500">*</span></Label>
                  <Select 
                    value={uploadConfig.quantityColumn.toString()} 
                    onValueChange={(v) => setUploadConfig(prev => ({ ...prev, quantityColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="po-quantity-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {uploadPreview.column_letters?.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>SKU Column <span className="text-slate-400 text-xs">(Optional)</span></Label>
                  <Select 
                    value={uploadConfig.skuColumn.toString()} 
                    onValueChange={(v) => setUploadConfig(prev => ({ ...prev, skuColumn: parseInt(v) }))}
                  >
                    <SelectTrigger data-testid="po-sku-column-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Not Used</SelectItem>
                      {uploadPreview.column_letters?.map((letter, idx) => (
                        <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                          Column {letter} ({idx + 1})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Raw Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Table className="w-4 h-4" />
                  <Label>File Preview (First 15 rows)</Label>
                </div>
                <div className="border rounded-lg overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-slate-600 w-16">Line</th>
                        {uploadPreview.column_letters?.map((letter, idx) => (
                          <th key={letter} className={`px-2 py-1.5 text-left font-medium min-w-[100px] ${
                            idx + 1 === uploadConfig.itemNameColumn ? 'bg-green-100 text-green-700' :
                            idx + 1 === uploadConfig.quantityColumn ? 'bg-purple-100 text-purple-700' :
                            idx + 1 === uploadConfig.skuColumn && uploadConfig.skuColumn > 0 ? 'bg-blue-100 text-blue-700' :
                            'text-slate-600'
                          }`}>
                            {letter}
                            {idx + 1 === uploadConfig.itemNameColumn && <span className="ml-1">(Name)</span>}
                            {idx + 1 === uploadConfig.quantityColumn && <span className="ml-1">(Qty)</span>}
                            {idx + 1 === uploadConfig.skuColumn && uploadConfig.skuColumn > 0 && <span className="ml-1">(SKU)</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.preview_rows?.slice(0, 15).map((row) => (
                        <tr 
                          key={row.line} 
                          className={`border-t ${
                            row.line === uploadConfig.headerLine ? 'bg-yellow-50 font-medium' : 
                            row.line > uploadConfig.headerLine ? 'bg-white' : 'bg-slate-50 text-slate-400'
                          }`}
                        >
                          <td className="px-2 py-1.5 font-mono">
                            {row.line}
                            {row.line === uploadConfig.headerLine && (
                              <Badge variant="secondary" className="ml-1 text-[10px] py-0">Header</Badge>
                            )}
                          </td>
                          {row.data.map((cell, idx) => (
                            <td key={idx} className={`px-2 py-1.5 truncate max-w-[150px] ${
                              idx + 1 === uploadConfig.itemNameColumn ? 'bg-green-50' :
                              idx + 1 === uploadConfig.quantityColumn ? 'bg-purple-50' :
                              idx + 1 === uploadConfig.skuColumn && uploadConfig.skuColumn > 0 ? 'bg-blue-50' : ''
                            }`}>
                              {cell || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mapped Preview */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  <Label>Mapped Data Preview</Label>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900 text-white">
                      <tr>
                        <th className="px-3 py-2 text-left">Item Name</th>
                        <th className="px-3 py-2 text-right">Order Qty</th>
                        {uploadConfig.skuColumn > 0 && <th className="px-3 py-2 text-left">SKU</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {getMappedPOPreview().length > 0 ? (
                        getMappedPOPreview().map((row, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-3 py-2 font-medium">{row.itemName || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono">{row.quantity || '—'}</td>
                            {uploadConfig.skuColumn > 0 && <td className="px-3 py-2 font-mono text-sm text-slate-500">{row.sku || '—'}</td>}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={uploadConfig.skuColumn > 0 ? 3 : 2} className="px-3 py-4 text-center text-slate-400">
                            No data rows found. Check your header line setting.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-500">
                  Showing first 5 data rows (starting from line {uploadConfig.headerLine + 1})
                  {uploadConfig.skuColumn === 0 && <span className="text-slate-400"> • SKU will be auto-generated</span>}
                </p>
              </div>
            </div>
          )}

          {uploading && (
            <div className="px-4 pb-4">
              <UploadProgress progress={uploadProgress} message="Creating orders from PO..." />
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={resetUploadDialog} disabled={uploading}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadPO} 
              disabled={!uploadFile || uploading || !uploadPreview}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-upload-po-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload & Create Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Delivery Dialog */}
      <Dialog open={showDeliveryDialog} onOpenChange={setShowDeliveryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-green-600" />
              Record Delivery
            </DialogTitle>
            <DialogDescription>
              Record a partial or full delivery for this order
            </DialogDescription>
          </DialogHeader>
          
          {deliveryOrder && (
            <div className="space-y-4 py-4">
              {/* Order Summary */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                <p className="font-semibold text-lg">{deliveryOrder.item_name}</p>
                <p className="text-sm text-slate-500">SKU: {deliveryOrder.sku}</p>
                <div className="flex gap-6 mt-3">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{deliveryOrder.total_quantity}</p>
                    <p className="text-xs text-slate-500">Total Order</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{deliveryOrder.total_delivered || 0}</p>
                    <p className="text-xs text-slate-500">Already Delivered</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{deliveryOrder.remaining}</p>
                    <p className="text-xs text-slate-500">Remaining</p>
                  </div>
                </div>
              </div>

              {/* Delivery Quantity */}
              <div className="space-y-2">
                <Label>Delivery Quantity <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  placeholder="Enter quantity being delivered"
                  value={deliveryQty}
                  onChange={(e) => setDeliveryQty(e.target.value)}
                  min="1"
                  max={deliveryOrder.remaining}
                  data-testid="delivery-qty-input"
                />
                <p className="text-xs text-slate-500">Max: {deliveryOrder.remaining} units</p>
              </div>

              {/* Recipient Depot (Optional) */}
              <div className="space-y-2">
                <Label>Recipient Depot <span className="text-slate-400 text-xs">(Optional)</span></Label>
                <Select value={deliveryDepot || "none"} onValueChange={(v) => setDeliveryDepot(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="delivery-depot-select">
                    <SelectValue placeholder="Select depot (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- All / Multiple Depots --</SelectItem>
                    {depots.map(depot => (
                      <SelectItem key={depot.id} value={depot.id}>{depot.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes (Optional) */}
              <div className="space-y-2">
                <Label>Notes <span className="text-slate-400 text-xs">(Optional)</span></Label>
                <Input
                  placeholder="e.g., Shipped via Truck #123"
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  data-testid="delivery-notes-input"
                />
              </div>

              {/* Warning if partial */}
              {parseInt(deliveryQty) > 0 && parseInt(deliveryQty) < deliveryOrder.remaining && (
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg text-sm text-yellow-700">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Partial delivery: {deliveryOrder.remaining - parseInt(deliveryQty)} units will remain pending
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliveryDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordDelivery}
              disabled={recordingDelivery || !deliveryQty || parseInt(deliveryQty) <= 0}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-delivery-btn"
            >
              {recordingDelivery ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <PackageCheck className="w-4 h-4 mr-2" />
                  Record Delivery
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
